import axios from 'axios'
import { ElMessage } from 'element-plus'
import router from '@/router';
import { authState } from '@/stores/authState'

// 修改为后端服务器域名下的端口 时间0806
const service = axios.create({
    baseURL: '/api', // 确保与后端端口一致
    timeout: 10000,  // 请求超时时间
})

// 获取token的函数
const getToken = () => {
    return localStorage.getItem('auth_token')
}

// 请求拦截器
service.interceptors.request.use(
    config => {
        // 在请求头中添加token
        const token = getToken()
        // console.log("请求头中获取到的token", token)
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`
        }

        // 为文件上传请求设置更长的超时时间
        if (config.url?.includes('/file/upload') ||
            config.url?.includes('/upload') ||
            (config.data instanceof FormData)) {
            config.timeout = 600000; // 5分钟超时
        }

        // ★ V2-45-3 幂等：对写操作（POST/PUT/DELETE）自动带 X-Idempotency-Key。
        //   后端 common/idempotent/IdempotentAspect 会用它做 Redis SETNX，
        //   防止"连点立即购买"或网络重试产生重复订单。
        //   用法约定：同一个 key 只在一次成功请求后被复用；
        //   失败的请求后端会释放锁，用户可立即重试。
        const method = (config.method || 'get').toLowerCase()
        if (['post', 'put', 'delete'].includes(method) && !config.headers['X-Idempotency-Key']) {
            config.headers['X-Idempotency-Key'] = uuid()
        }

        return config
    },
    error => {
        return Promise.reject(error)
    }
)

/** 简单 uuid（够用即可，不用于安全场景） */
function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}


// 响应拦截器
service.interceptors.response.use(
    response => {
        // 统一处理响应
        const res = response.data

        // 如果业务码为200，直接返回数据
        if (res.code === '200') {
            // 检查是否是登录相关的请求
            const isLoginRequest = response.config.url?.includes('/auth/login') ||
                response.config.url?.includes('/auth/wechat/login/status')

            const isAdminLogin = response.config.url?.includes('/auth/login/admin')

            if (isLoginRequest && res.data?.token) {
                // 如果是管理员登录，我们不在这里处理跳转，由登录页面自己处理
                if (isAdminLogin) {
                    // 管理员登录的跳转已经在AdminLoginPage.vue中处理，这里不做任何事
                } else {
                    // 普通用户登录，执行原来的跳转逻辑
                    const redirect = getRedirectPath()
                    if (redirect) {
                        // 使用setTimeout确保在响应处理完成后跳转
                        setTimeout(() => {
                            router.push(redirect)
                            // 清除存储的重定向路径
                            clearRedirectPath()
                        }, 0)
                    } else {
                        // 没有重定向路径，跳转到首页
                        setTimeout(() => {
                            router.push('/home')
                        }, 0)
                    }
                }
            }

            return res
        } else {
            // 业务错误处理
            return Promise.reject(res)
        }
    },
    error => {

        if (error.response && error.response.status === 401) {

            // 检查请求配置中是否有 _skipRedirectOn401 标记(专用于公共插件付费激活页面--publicplugindetail页面)
            const skipRedirect = error.config && error.config._skipRedirectOn401;

            // 如果设置了跳过，则不执行跳转，仅清除 token 并返回错误
            if (skipRedirect) {
                // 清除无效 token 和用户状态
                localStorage.removeItem('auth_token');
                authState.clear();
                // 直接返回错误，让调用方处理
                return Promise.reject(error);
            }

            const isAdmin = localStorage.getItem('is_admin') === 'true';

            // ★ 不再对 /user/current 的 401 做重试。
            //   原实现会退避重试 3 次（500/1000/1500ms），但重试用的是同一个已经过期的 token，
            //   服务端 validateToken() 的结果是确定性的，重试 100 次也是 401。
            //   实测一次 token 过期 ⇒ 4 个请求 × 后端 2 条日志 = 8 行 ERROR，还会拖 3 秒才跳登录。
            //   令牌在签发时就写入 Redis，不存在"刚登录还没同步"的竞态，所以无需兜底重试。

            // 重置全局用户状态
            authState.clear();

            // history 模式下 window.location.hash 为空，必须从 router 取当前路径
            const currentPath = router.currentRoute.value.fullPath || '/';

            // 登录页本身不跳转（否则会无限刷新）
            const onLoginPage = currentPath === '/login'
                || currentPath === '/admin/login'
                || currentPath === '/bind-phone';

            // ★ 原实现在首页(/ 与 /home)既不跳转也不提示 ⇒ 用户表现为"点了没反应，也不报错"。
            //   现改为：只要不是登录页，就一定给出反馈。
            if (!onLoginPage) {
                if (isAdmin) {
                    ElMessage.error('管理员登录已过期，请重新登录');
                    // 清除管理员标志
                    authState.setIsAdmin(false);
                    // 保存重定向路径
                    saveRedirectPath(currentPath)
                    // 跳转到管理员登录页
                    router.push('/admin/login');
                } else {
                    // 普通用户，跳转到普通登录页（首页等也要跳，并给出提示）
                    ElMessage.error('登录已过期，请重新登录');
                    // 保存重定向路径
                    saveRedirectPath(currentPath)
                    // 跳转到登录页
                    router.push('/login');
                }
            }
        }

        return Promise.reject(error)
    }
)

// 保存重定向路径到sessionStorage
const saveRedirectPath = (path) => {
    sessionStorage.setItem('redirect_path', path)
}

// 获取重定向路径
const getRedirectPath = () => {
    return sessionStorage.getItem('redirect_path')
}

// 清除重定向路径
const clearRedirectPath = () => {
    sessionStorage.removeItem('redirect_path')
}

// 封装请求方法
const request = {
    get(url, config = {}) {
        return service.get(url, config)
    },
    post(url, data, config = {}) {
        return service.post(url, data, config)
    },
    put(url, data, config = {}) {
        return service.put(url, data, config)
    },
    delete(url, config = {}) {
        return service.delete(url, config)
    }
}


export default request

/**
 * 创建一个使用自定义 token 的请求实例。
 *
 * 用途：ExePricing.vue（exe 产品线的落地页）走 exe 客户端自己的 JWT，
 * 不能用 localStorage 里的 auth_token，所以它原先自己写了一套 fetch。
 * 本工厂把那套逻辑收敛到这里，与默认 request 保持同一套响应约定。
 *
 * ⚠️ 响应结构与默认 request 一致：拦截器已返回 response.data（业务 Result 对象），
 *    调用处应直接取 result.data，不要再写 response.data.data。
 */
export function createRequest({ tokenProvider, baseURL = '/api', timeout = 10000 } = {}) {
    const inst = axios.create({ baseURL, timeout })

    inst.interceptors.request.use(config => {
        const tk = tokenProvider ? tokenProvider() : getToken()
        if (tk) {
            config.headers['Authorization'] = `Bearer ${tk}`
        }
        // 文件上传给足超时时间（与默认 request 保持一致）
        if (config.url?.includes('/upload') || (config.data instanceof FormData)) {
            config.timeout = 600000
        }
        return config
    })

    inst.interceptors.response.use(
        response => {
            const res = response.data
            // 与默认 request 一致：业务码 200 才 resolve，否则 reject
            return res.code === '200' ? res : Promise.reject(res)
        },
        error => Promise.reject(error)
    )

    return {
        get: (url, config = {}) => inst.get(url, config),
        post: (url, data, config = {}) => inst.post(url, data, config),
        put: (url, data, config = {}) => inst.put(url, data, config),
        delete: (url, config = {}) => inst.delete(url, config)
    }
}